'use client';

import {type FC, memo} from 'react';
import {usePointsAnimation} from '@/hooks/usePointsAnimation';
import {formatNumberShort} from '@/utils/helpers/formatNumberShort';

interface AnimatedPointsCounterProps {
  value: number;
  className?: string;
  mobileClassName?: string;
  formatOptions?: {
    decimals?: number;
    floor?: boolean;
    hideZero?: boolean;
  };
  animateIntegerOnly?: boolean;
}

/**
 * Компонент для отображения анимированного счетчика поинтов
 * @param value - Текущее значение поинтов
 * @param className - Классы для десктопной версии
 * @param mobileClassName - Классы для мобильной версии
 * @param formatOptions - Опции форматирования числа
 * @param animateIntegerOnly - При включении анимируется только целая часть числа
 */
export const AnimatedPointsCounter: FC<AnimatedPointsCounterProps> = memo(
  ({value, className, mobileClassName, formatOptions = {}, animateIntegerOnly = false}) => {
    const {animatedValue} = usePointsAnimation({
      currentValue: value,
      animationDuration: 3000,
    });

    const fractionalPart = animateIntegerOnly ? value % 1 : 0;
    const animatedDisplayValue = animateIntegerOnly ? Math.trunc(animatedValue) + fractionalPart : animatedValue;

    const formattedValue = formatNumberShort(
      animatedDisplayValue,
      formatOptions.decimals,
      formatOptions.floor,
      'floor',
      formatOptions.hideZero ?? true,
    );

    const mobileFormattedValue = formatNumberShort(
      animatedDisplayValue,
      formatOptions.decimals || 4,
      formatOptions.floor || false,
      'floor',
      formatOptions.hideZero ?? true,
    );

    return (
      <>
        <span className={className}>{formattedValue}</span>
        <span className={mobileClassName}>{mobileFormattedValue}</span>
      </>
    );
  },
);

AnimatedPointsCounter.displayName = 'AnimatedPointsCounter';
